<template>
  <div>
    <div
      class="-full-preview-main"
      @mousemove="hoverOnVideo = true"
      @mouseleave="hoverOnVideo = false"
    >
      <div
        :class="[
          '-full-preview-header',
          {
            '-playable': isPlayable,
            '-active': isPlayable && (!isPlaying || hoverOnVideo),
          },
        ]"
      >
        <wt-close
          class="-close wt2-color-negative"
          @click.native="closeModal"
        />
      </div>

      <div
        v-show="isCaptureHover && isPlayable && !hasVisualGap"
        class="-capture-hover"
      >
        <i class="wticon wticon-capture-camera-icon" />
      </div>

      <div class="-capture-hover -animate" v-show="isTakingSnapshot">
        <i class="wticon wticon-capture-camera-icon" />
        <img :src="canvasScreenshot.image" />
      </div>

      <transition name="wt2-fade" mode="out-in">
        <generating-state v-if="isGenerating" class="-full-preview-states" />
        <playable-state
          v-else-if="isPlayable"
          ref="playableState"
          class="-full-preview-states"
          :job="fullPreviewJob"
          :parts="parts"
          :story="story"
          :use-url="showGeneratedLink"
          :url="previewVideoUrl"
          :is-playing.sync="isPlaying"
        />
        <error-state
          v-else-if="isError"
          class="-full-preview-states"
          :video-id="story.id"
        />
      </transition>
    </div>

    <footer v-if="hasVisualGap" class="-message-board -warn">
      <i class="wticon wticon-information1" />
      <div>{{ 'fullPreview.HAS_GAPS' | culture }}</div>
    </footer>

    <footer v-if="hasPendingCC && !hasVisualGap" class="-message-board -warn">
      <i class="wticon wticon-information1" />
      <div>{{ 'fullPreview.CC_BLOCK' | culture }}</div>
    </footer>

    <footer
      v-if="hasEmptyTranscript && !hasPendingCC && !hasVisualGap"
      class="-message-board -info"
    >
      <i class="wticon wticon-information1" />
      <div>{{ 'fullPreview.CC_EMPTY_TRANSCRIPT' | culture }}</div>
    </footer>

    <thumbnail-area
      :videoPoster="videoPoster"
      :hasGaps="hasVisualGap"
      :isPlayable="isPlayable"
      :story="story"
      :isCaptureHover.sync="isCaptureHover"
      :captureSnapshot="onCaptureSnapshot"
      :canvasScreenshot="canvasScreenshot"
      :producedBlocked="producedBlocked"
      :isTakingSnapshot="isTakingSnapshot"
      @pause-playing="onPausePlaying"
    />
  </div>
</template>

<script>
import app from 'app';
import generatingState from './fullPreviewGeneratingState.vue';
import playableState from './fullPreviewPlayableState.vue';
import errorState from './fullPreviewErrorState.vue';
import thumbnailArea from './fullPreviewThumbnailArea.vue';
import { mapGetters, mapState } from 'vuex';
import storyViewBus from 'v/Story/storyViewBus';

export default {
  name: 'preview-maker',

  components: {
    generatingState,
    playableState,
    errorState,
    thumbnailArea,
  },

  props: {
    closeModal: { type: Function, required: true },
    valid: { type: Boolean },
    story: { type: Object, required: true },
    showGeneratedLink: { type: Boolean },
    previewVideoUrl: { type: String },
    fullPreviewJob: { type: Object },
    screenshot: { type: [Object, Number], required: true },
  },

  data() {
    return {
      hoverOnVideo: false,
      isPlaying: false,
      videoPoster: {},
      canvasScreenshot: this.screenshot,
      curState: null,
      producedBlocked: false,
      parts: {},
      isCaptureHover: false,
      isTakingSnapshot: false,
      transcriptIdsSet: new Set(),
      hasPendingCC: false,
      hasEmptyTranscript: false,
    };
  },

  computed: {
    ...mapState({
      transcriptions: (state) => state.story.transcriptions,
      enableVOSubtitles: (state) => state.story.enableVOSubtitles,
    }),
    ...mapGetters('timemachine', ['hasVisualGap']),

    states() {
      return {
        GENERATING: 'generating',
        PLAYABLE: 'playable',
        ERROR: 'error',
      };
    },

    previewMakerOk() {
      return (
        !this.hasVisualGap &&
        !this.hasPendingCC &&
        !this.isError &&
        (!!this.canvasScreenshot.time || !!this.videoPosterAvailable)
      );
    },

    videoPosterAvailable() {
      return this.videoPoster && this.videoPoster.metadata;
    },

    isGenerating() {
      return this.curState === this.states.GENERATING;
    },

    isPlayable() {
      return this.curState === this.states.PLAYABLE;
    },

    isError() {
      return this.curState === this.states.ERROR;
    },
  },

  beforeDestroy() {
    app.stopListening(
      this.story.videoPosterModel,
      'change',
      this.videoPosterModified
    );
    this.fullPreviewJob.off('change:parts fullPreviewFailed');
  },

  created() {
    this.videoPoster = this.story.videoPosterModel.toJSON();
    app.listenTo(
      this.story.videoPosterModel,
      'change',
      this.videoPosterModified
    );

    if (this.hasVisualGap) {
      storyViewBus.$emit('svbShowVisualAudioGap');
    }

    _.each(app.timelineGateway.getAudioData(), (asset) => {
      this.transcriptIdsSet.add(asset.assetUUID);
    });
    _.each(app.timelineGateway.getVisualData(), (asset) => {
      if (asset.agd.showTranscription) {
        this.transcriptIdsSet.add(asset.metadata.assetUUID);
      }
    });
    this.determineClosedCaptions();
  },

  mounted() {
    this.$_determinePreviewState();
  },

  watch: {
    previewVideoUrl(value) {
      this.story.set('_previewVideoUrl', value);
    },

    previewMakerOk: {
      immediate: true,
      handler(value) {
        this.producedBlocked = !value;
        this.$emit('update:valid', value);
      },
    },

    canvasScreenshot(value) {
      this.$emit('update:screenshot', value);
    },

    transcriptions: {
      deep: true,
      handler() {
        this.determineClosedCaptions();
      },
    },
  },

  methods: {
    $_determinePreviewState() {
      // playable + show link
      if (this.fullPreviewJob.isDone) {
        this.setCurState(this.states.PLAYABLE);
      } else {
        // playable/generating - listen to changes
        if (!_.isEmpty(this.fullPreviewJob.get('parts'))) {
          this.parts = this.fullPreviewJob.get('parts');
          this.setCurState(this.states.PLAYABLE);
        } else {
          this.setCurState(this.states.GENERATING);
        }

        this.fullPreviewJob.once('fullPreviewFailed', (how) => {
          app.doctorController.report(`fullPreviewFailed: '${how}'`);
          // reset parts
          this.parts = {};
          // remove job from the list, because it's caused failure
          app.api.fullPreview.currentJob.stopListenToUpdates();
          app.api.fullPreview.currentJob = null;
          this.setCurState(this.states.ERROR);
        });

        this.fullPreviewJob.on('change:parts', (job) => {
          this.parts = job.get('parts');

          if (!this.isPlayable) {
            const partsKeys = Object.keys(this.parts);

            // change to playable state when there is audio and at one other part,
            // so it could render immediately
            if (partsKeys.length > 1 && partsKeys.includes('AUDIO')) {
              this.setCurState(this.states.PLAYABLE);
            }
          }
        });
      }
    },

    determineClosedCaptions() {
      const allowedPlTranscription = app.timelineGateway.programmer.get(
        'allowedTranscription'
      );

      if (this.enableVOSubtitles && allowedPlTranscription) {
        this.hasPendingCC = _.some([...this.transcriptIdsSet], (assetUUID) => {
          return /PENDING|PROCESSING/.test(
            this.transcriptions[assetUUID]?.status
          );
        });

        this.hasEmptyTranscript = _.some(
          [...this.transcriptIdsSet],
          (assetUUID) => {
            const transcript = this.transcriptions[assetUUID];
            const isEmpty = !transcript || _.isEmpty(transcript.tokens);
            const isRemoved =
              !isEmpty &&
              transcript.tokens.length === 1 &&
              transcript.tokens[0].type === 'GAP';
            return isEmpty || isRemoved;
          }
        );
      }
    },

    setCurState(newState) {
      // if state is already curState, return.
      if (this.curState === newState) {
        return;
      }

      this.curState = newState;
    },

    onCaptureSnapshot() {
      if (this.isTakingSnapshot || !this.$refs.playableState) {
        return;
      }
      this.isCaptureHover = false;
      this.isTakingSnapshot = true;
      _.delay(() => {
        this.isTakingSnapshot = false;
      }, 1250);
      this.$set(
        this,
        'canvasScreenshot',
        this.$refs.playableState.takeScreenshot()
      );
    },

    onPausePlaying() {
      this.$refs.playableState && this.$refs.playableState.pausePlay();
    },

    videoPosterModified(videoPosterModel) {
      this.$set(this, 'canvasScreenshot', {
        image: null,
        time: null,
      });
      this.videoPoster = videoPosterModel.toJSON();
    },
  },
};
</script>
